import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FluentProvider, fluentProviderClassNames, makeStaticStyles } from '@fluentui/react-components';
import { darkTheme, lightTheme } from './tokens.js';
import { shellMedia, shellMetrics } from './shell/tokens.js';
/**
 * Global reset. Raw values are permitted here (packages/ui is the single place
 * they may live) so no feature or app stylesheet needs them.
 */
const useGlobalStyles = makeStaticStyles({
    'html, body, #root': {
        height: '100%',
    },
    body: {
        margin: 0,
    },
    /**
     * The provider fills the page. This is a GLOBAL rule scoped to the provider inside #root
     * on purpose: Fluent copies the provider's className onto every portal mount node it
     * appends to <body> (tooltips, menus, dialogs), and those nodes carry the theme's opaque
     * background. A `className` prop with `min-height: 100%` therefore reached the tooltip
     * mount nodes and turned them into full-page white sheets over the app — the rail
     * tier, the only one with tooltips, rendered blank in Edge (checkpoint 4 QA).
     */
    [`#root > .${fluentProviderClassNames.root}`]: {
        minHeight: '100%',
    },
    /**
     * Reference control geometry (Figma 1:567 button 40, 258:4840 field 36): applied once here
     * so every Fluent control in every feature picks it up. Scoped under the provider so the
     * rules beat Fluent's atomic classes and never reach portal mount nodes' children twice.
     */
    [`.${fluentProviderClassNames.root} .fui-Button`]: { minHeight: '40px' },
    [`.${fluentProviderClassNames.root} .fui-Input, .${fluentProviderClassNames.root} .fui-Combobox, .${fluentProviderClassNames.root} .fui-Dropdown, .${fluentProviderClassNames.root} .fui-SpinButton`]: { minHeight: '36px' },
    '*, *::before, *::after': {
        boxSizing: 'border-box',
    },
});
const ThemeContext = createContext(undefined);
function preferredScheme() {
    if (typeof window === 'undefined' || !window.matchMedia)
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
const usePhoneTargetStyles = makeStaticStyles(`${shellMedia.phone} {
    .${fluentProviderClassNames.root} a[data-document-link] { display: inline-flex; align-items: center; min-height: ${shellMetrics.touchTarget}; }
    .${fluentProviderClassNames.root} .fui-Checkbox { min-height: ${shellMetrics.touchTarget}; }
    .${fluentProviderClassNames.root} .fui-Checkbox__input { min-height: ${shellMetrics.touchTarget}; min-width: ${shellMetrics.touchTarget}; }
    .${fluentProviderClassNames.root} .fui-Button, .${fluentProviderClassNames.root} .fui-Input, .${fluentProviderClassNames.root} .fui-Combobox, .${fluentProviderClassNames.root} .fui-Dropdown, .${fluentProviderClassNames.root} .fui-Select, .${fluentProviderClassNames.root} .fui-Select__select { min-height: ${shellMetrics.touchTarget}; }
    .${fluentProviderClassNames.root} .fui-Select, .${fluentProviderClassNames.root} .fui-Select__select { width: 100%; min-width: 0; max-width: 100%; }
  }`);
export function ThemeProvider({ children }) {
    useGlobalStyles();
    usePhoneTargetStyles();
    const [scheme, setScheme] = useState(preferredScheme);
    const toggle = useCallback(() => {
        setScheme((current) => (current === 'light' ? 'dark' : 'light'));
    }, []);
    const value = useMemo(() => ({ scheme, toggle }), [scheme, toggle]);
    return (_jsx(ThemeContext.Provider, { value: value, children: _jsx(FluentProvider, { theme: scheme === 'light' ? lightTheme : darkTheme, children: children }) }));
}
export function useColorScheme() {
    const context = useContext(ThemeContext);
    if (!context)
        throw new Error('useColorScheme must be used inside <ThemeProvider>');
    return context;
}
//# sourceMappingURL=ThemeProvider.js.map